import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DirectoryProviderCard } from "@/components/directory-page";
import { directoryRegions } from "@/data/directory-pages";
import { regionalServices } from "@/data/regional-services";
import { getPublicProviders } from "@/lib/public-content.functions";
import { breadcrumbLd, jsonLdScript, SITE_URL } from "@/lib/json-ld";
import {
  regionalAccessAdvice,
  regionalServicePageInfo,
  regionalServiceProviders,
  regionalServiceSlug,
  resolveRegionalServicePage,
} from "@/lib/regional-services";

export const Route = createFileRoute("/$serviceRegion")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const page = resolveRegionalServicePage(params.serviceRegion);
    if (!page) throw notFound();
    const { providers, unavailable } = await getPublicProviders({
      data: { type: "auftragnehmer" },
    });
    return {
      ...page,
      providers: regionalServiceProviders(providers, page.service, page.region),
      unavailable,
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Leistung nicht gefunden | Begner" },
          { name: "robots", content: "noindex" },
        ],
      };
    const { service, region, providers } = loaderData;
    const info = regionalServicePageInfo(service, region);
    return {
      meta: [
        { title: info.title },
        { name: "description", content: info.description },
        { property: "og:title", content: info.title },
        { property: "og:description", content: info.description },
        { property: "og:url", content: `${SITE_URL}${info.path}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: [
        jsonLdScript(
          breadcrumbLd([
            { name: "Startseite", path: "/" },
            { name: "Leistungen nach Kanton", path: "/leistungen" },
            { name: info.heading, path: info.path },
          ]),
        ),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: info.heading,
          description: info.description,
          url: `${SITE_URL}${info.path}`,
          inLanguage: "de-CH",
          spatialCoverage: { "@type": "AdministrativeArea", name: `Kanton ${region.name}` },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: providers.length,
            itemListElement: providers.map((provider, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "Organization",
                name: provider.name,
                url: `${SITE_URL}${info.path}#anbieter-${provider.id}`,
              },
            })),
          },
        }),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Leistung oder Kanton nicht gefunden</h1>
      <Link
        to="/leistungen"
        className="mt-6 inline-block font-semibold text-brand-deep hover:underline"
      >
        Leistungen nach Kanton ansehen
      </Link>
    </div>
  ),
  component: RegionalServicePage,
});

function RegionalServicePage() {
  const { service, region, providers, unavailable } = Route.useLoaderData();
  const info = regionalServicePageInfo(service, region);
  const related = regionalServices.filter(
    (s) => s.family === service.family && s.slug !== service.slug,
  );
  const faqs = [{ question: service.costQuestion, answer: service.costAnswer }, service.faq];
  return (
    <>
      <section className="border-b border-border bg-surface py-12 md:py-16">
        <div className="container-page max-w-5xl">
          <nav
            aria-label="Brotkrümelnavigation"
            className="mb-7 flex flex-wrap gap-2 text-sm text-muted-foreground"
          >
            <Link to="/" className="hover:underline">
              Startseite
            </Link>
            <span aria-hidden="true">/</span>
            <Link to="/leistungen" className="hover:underline">
              Leistungen
            </Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{info.heading}</span>
          </nav>
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Kanton {region.name}
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
            {info.heading}
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">{service.intro}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-xl">
              <a href="#firmen">
                Firmenprofile ansehen <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl bg-card">
              <Link to="/auftrag-einstellen">Offerten kostenlos anfragen</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="firmen" className="scroll-mt-24 py-12">
        <div className="container-page max-w-5xl">
          <h2 className="font-display text-2xl font-bold">
            Firmen für {service.name} im Kanton {region.name}
          </h2>
          <p className="mt-4 max-w-3xl leading-7 text-muted-foreground">
            Die Auswahl berücksichtigt veröffentlichte Standortangaben im Kanton {region.name} und
            passende Kategorien oder Leistungen. Bestätigen Sie Einsatzgebiet und genauen
            Auftragsumfang direkt mit dem Anbieter.
          </p>
          <p role="status" className="mt-5 text-sm text-muted-foreground">
            {unavailable
              ? "Die Firmenprofile sind momentan nicht abrufbar."
              : `${providers.length} ${providers.length === 1 ? "Firmenprofil" : "Firmenprofile"} gefunden`}
          </p>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {providers.map((provider) => (
              <DirectoryProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
          {providers.length === 0 && (
            <div className="mt-5 rounded-2xl border border-dashed border-border bg-surface p-6">
              <h3 className="font-display text-xl font-bold">
                {unavailable
                  ? "Firmenprofile später erneut prüfen"
                  : "Aktuell keine passenden Profile veröffentlicht"}
              </h3>
              <p className="mt-3 leading-7 text-muted-foreground">
                {unavailable
                  ? "Die Hinweise zu Leistung, Kosten und regionaler Planung stehen Ihnen weiterhin zur Verfügung."
                  : "Im Kantonsverzeichnis können Sie weitere Firmenprofile prüfen. Ob ein Betrieb die gesuchte Leistung anbietet, klären Sie direkt mit ihm."}
              </p>
              <Link
                to="/handwerkerverzeichnis/$region"
                params={{ region: region.slug }}
                className="mt-4 inline-block font-semibold text-brand-deep hover:underline"
              >
                Firmenverzeichnis {region.name}
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="border-y border-border bg-surface py-12">
        <div className="container-page grid max-w-5xl gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold">
              {service.name} im Kanton {region.name} planen
            </h2>
            <p className="mt-4 leading-7 text-muted-foreground">
              {regionalAccessAdvice[region.canton]}
            </p>
            <p className="mt-4 leading-7 text-muted-foreground">
              Zum Kanton gehören unter anderem {region.locations.join(", ")}. Nennen Sie Gemeinde
              und Postleitzahl; eine Standortangabe im Profil bestätigt keine Verfügbarkeit im
              ganzen Kanton.
            </p>
          </div>
          <aside className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-xl font-bold">
              Diese Angaben helfen bei Ihrer Anfrage
            </h2>
            <ul className="mt-4 space-y-4">
              {service.preparation.map((item) => (
                <li key={item} className="flex gap-3 leading-7">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="py-12">
        <div className="container-page grid max-w-5xl gap-8 md:grid-cols-2">
          {service.sections.map((section) => (
            <div key={section.title}>
              <h2 className="font-display text-2xl font-bold">{section.title}</h2>
              <p className="mt-4 leading-7 text-muted-foreground">{section.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border py-12">
        <div className="container-page max-w-5xl">
          <h2 className="font-display text-2xl font-bold">
            {service.name} {region.name}: Kosten und Fragen
          </h2>
          <div className="mt-5 divide-y divide-border">
            {faqs.map((faq) => (
              <details key={faq.question} className="py-4">
                <summary className="cursor-pointer py-2 font-semibold">{faq.question}</summary>
                <p className="pb-2 pt-3 leading-7 text-muted-foreground">{faq.answer}</p>
              </details>
            ))}
          </div>
          <Button asChild className="mt-6 rounded-xl">
            <Link to="/auftrag-einstellen">
              Offerten für {service.name} anfragen <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <section className="border-t border-border bg-surface py-12">
        <div className="container-page max-w-5xl space-y-10">
          <div>
            <h2 className="font-display text-xl font-bold">
              Weitere Leistungen im Kanton {region.name}
            </h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {related.map((s) => (
                <Link
                  key={s.slug}
                  to="/$serviceRegion"
                  params={{ serviceRegion: regionalServiceSlug(s, region) }}
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand hover:text-brand-deep"
                >
                  {s.name} {region.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">{service.name} in anderen Kantonen</h2>
            <div className="mt-5 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {directoryRegions
                .filter((r) => r.slug !== region.slug)
                .map((r) => (
                  <Link
                    key={r.slug}
                    to="/$serviceRegion"
                    params={{ serviceRegion: regionalServiceSlug(service, r) }}
                    className="inline-flex min-h-11 items-center text-sm font-medium text-brand-deep hover:underline"
                  >
                    {service.name} {r.name}
                  </Link>
                ))}
            </div>
          </div>
          <Link
            to="/leistungen"
            className="inline-block font-semibold text-brand-deep hover:underline"
          >
            Alle Leistungen und Kantone ansehen
          </Link>
        </div>
      </section>
    </>
  );
}
